import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';

import {
  JoinUsPageType,
  OpportunitiesData,
} from 'containers/JoinUsPage/JoinUsPage.types';
import { useOpportunitiesStore } from 'store';
import { colors } from 'utils/styles/theme';

import * as S from './OpportunityListOverlay.styles';

export interface OpportunityListOverlayProps {
  data: OpportunitiesData;
  pageData: JoinUsPageType;
}

const defaultProps: Partial<OpportunityListOverlayProps> = {};

const OpportunityListOverlay: React.FunctionComponent<
  OpportunityListOverlayProps
> = ({ data, pageData, ...rest }) => {
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const { openOpportunity } = useOpportunitiesStore();

  const { push, pathname } = useRouter();

  const opportunities = useMemo(() => {
    return data.opportunities.filter(opportunity => {
      const areFiltersApplied =
        selectedDepartments.length > 0 || selectedLocations.length > 0;

      if (!areFiltersApplied) return true;

      let isFiltered = true;

      if (selectedDepartments.length > 0) {
        isFiltered = selectedDepartments.includes(opportunity.departments[0]);
      }

      if (selectedLocations.length > 0) {
        isFiltered = selectedLocations.includes(opportunity.offices[0]);
      }

      return isFiltered;
    });
  }, [data, selectedDepartments, selectedLocations]);

  const availableDepartments = useMemo(() => {
    if (selectedLocations.length === 0) return data.departments;
    const filteredDepartments = opportunities.map(
      opportunity => opportunity.departments[0]
    );

    return data.departments.filter(department =>
      filteredDepartments.includes(department)
    );
  }, [opportunities, data]);

  const availableLocations = useMemo(() => {
    if (selectedDepartments.length === 0) return data.locations;

    const filteredLocations = opportunities.map(
      opportunity => opportunity.offices[0]
    );

    return data.locations.filter(department =>
      filteredLocations.includes(department)
    );
  }, [opportunities, data]);

  const onDepartmentSelected = item => {
    const items = [...selectedDepartments];
    items.indexOf(item) === -1
      ? items.push(item)
      : items.splice(items.indexOf(item), 1);

    setSelectedDepartments(items);
  };

  const onLocationSelected = item => {
    const items = [...selectedLocations];
    items.indexOf(item) === -1
      ? items.push(item)
      : items.splice(items.indexOf(item), 1);

    setSelectedLocations(items);
  };

  const renderAppliedFilters = () =>
    (selectedDepartments.length > 0 || selectedLocations.length > 0) && (
      <S.AppliedFiltersWrapper>
        <S.Label>{pageData.jobOverlayAppliedFiltersLabel}</S.Label>
        <S.Row>
          {selectedLocations.map(filter => (
            <S.Badge
              key={`filter-${filter}`}
              onClick={() => {
                onLocationSelected(filter);
              }}
            >
              {filter}
              <svg
                width="8"
                height="8"
                viewBox="0 0 8 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.11115 0L8 0.888845L0.889237 7.99961L0.000391825 7.11076L7.11115 0Z"
                  fill={colors.lighterMineShaft}
                />
                <path
                  d="M7.99961 7.11155L7.11076 8.00039L0 0.889629L0.888845 0.0007841L7.99961 7.11155Z"
                  fill={colors.lighterMineShaft}
                />
              </svg>
            </S.Badge>
          ))}
        </S.Row>
        <S.Row>
          {selectedDepartments.map(filter => (
            <S.Badge
              key={`filter-${filter}`}
              onClick={() => {
                onDepartmentSelected(filter);
              }}
            >
              {filter}
              <svg
                width="8"
                height="8"
                viewBox="0 0 8 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.11115 0L8 0.888845L0.889237 7.99961L0.000391825 7.11076L7.11115 0Z"
                  fill={colors.lighterMineShaft}
                />
                <path
                  d="M7.99961 7.11155L7.11076 8.00039L0 0.889629L0.888845 0.0007841L7.99961 7.11155Z"
                  fill={colors.lighterMineShaft}
                />
              </svg>
            </S.Badge>
          ))}
        </S.Row>
      </S.AppliedFiltersWrapper>
    );

  const renderFilters = (
    <S.Filters>
      <S.FiltersLabel>{pageData.jobOverlayFilterLabel}</S.FiltersLabel>
      <S.FilterWrapper>
        <S.Label>{pageData.jobOverlayDepartmentLabel}</S.Label>
        <S.MultiSelectList
          options={data.departments}
          enabled={availableDepartments}
          selected={selectedDepartments}
          defaultSelection={pageData.jobOverlayDepartmentPlaceholder}
          onSelected={onDepartmentSelected}
        />
      </S.FilterWrapper>
      <S.FilterWrapper>
        <S.Label>{pageData.jobOverlayLocationLabel}</S.Label>
        <S.MultiSelectList
          options={data.locations}
          selected={selectedLocations}
          enabled={availableLocations}
          defaultSelection={pageData.jobOverlayLocationPlaceholder}
          onSelected={onLocationSelected}
        />
      </S.FilterWrapper>
      {renderAppliedFilters()}
    </S.Filters>
  );

  const renderOpportunities = () => (
    <>
      <S.Counter>
        {opportunities.length} {pageData.jobOverlayResultsLabel}
      </S.Counter>
      <S.Opportunities>
        {opportunities.map(opportunity => (
          <S.Opportunity
            style={{ backgroundColor: colors.concrete }}
            whileHover={{ backgroundColor: colors.mercury }}
            onClick={() => {
              push(
                `${pathname}?job=${opportunity.internal_job_id}`,
                undefined,
                { shallow: true }
              );
              openOpportunity(opportunity);
            }}
            key={`opportunity-${opportunity.id}`}
          >
            <S.OpportunityTitle>{opportunity.title}</S.OpportunityTitle>
            <S.JobDataWrapper>
              <S.Text>{opportunity.departments[0]}</S.Text>
              <S.Text>{opportunity.offices[0]}</S.Text>
              <S.Text>
                {opportunity.metadata[0].value
                  ? opportunity.metadata[0].value
                  : '-'}
              </S.Text>
            </S.JobDataWrapper>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.38867 0.869141L23.0015 0.934142L23.0015 0.934804L23.0029 0.934809L23.0679 18.5476L20.6216 18.5386L20.5719 5.06051L1.94511 23.6873L0.208985 21.9512L18.7953 3.36481L5.3977 3.31536L5.38867 0.869141Z"
              />
            </svg>
          </S.Opportunity>
        ))}
      </S.Opportunities>
    </>
  );

  return (
    <S.OpportunityOverlayWrapper
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      {...rest}
    >
      <S.Title>Opportunities</S.Title>
      {renderFilters}
      {renderOpportunities()}
    </S.OpportunityOverlayWrapper>
  );
};

OpportunityListOverlay.defaultProps = defaultProps;

export default OpportunityListOverlay;
